import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import React, { useState, useMemo, useRef, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { PremiumTrash } from '@wix/da-ds/pkg/Icons/Confirmations';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import noop from '../../../../utils/noop';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { useDeviationAttributes } from '../../../hooks/useDeviationAttributes';
import UserAvatar from '../../../../User/Avatar';
import UserLink from '../../../../User/Link';
// TODO: move this out of WatchSuggestions
import UserWatchGhostButton from '../../../../WatchSuggestions/UserWatchGhostButton';
import Timestamp from '../../../../Timestamp';
import ConfirmationModal from '../../../../Popup/ConfirmationModal';
import { getDeviationEntityId } from '../../../../redux/normalizr/schemas/deviation';
import useDuperbrowseLink from '../../../../Duperbrowse/hooks/useDuperbrowseLink';
import { DeviationLink } from '../../../../Duperbrowse';
import ThumbActions from '../../../Helper/ThumbActions';
import { getDeviationType, getDeviationUrl, deviationTypeToTranslationKey, getDeviationResourceType } from '../../../_helpers';
import PriceAndSubscribeButton from '../PriceAndSubscribeButton';
import s from './FeedCardHeader.scss';
import { BiData } from '@wix/da-bi/pkg/events';
const subscriptionReferralType = {
    referral_info: ReferralType.LOCKED_CONTENT
};
const FeedCardHeader = ({ deviation, className, extraActions = [], disableScrollRestoration, withWatchOrSubscribeButton = true, showReportModal = noop, deleteDeviation = noop })=>{
    const { t } = useTranslation();
    const logBiEvent = useBiLogger();
    const isDesktop = !useContext(MobileContext);
    const [isShowingConfirmation, setShowingConfirmation] = useState(false);
    const { isOwner, tier, needsSubscription } = useSubscriptionTier(deviation);
    const type = getDeviationType(deviation);
    const typeName = t(deviationTypeToTranslationKey(type));
    // add standard post actions: report and go to page
    const { isReportable } = useDeviationAttributes(deviation, isOwner);
    // duperbrowse for the canonical link
    const duperRef = useRef(null);
    const onDuperbrowseClick = useDuperbrowseLink(getDeviationEntityId(deviation), duperRef, undefined, disableScrollRestoration);
    let watchOrSubscribeButton = null;
    if (isDesktop && withWatchOrSubscribeButton) {
        watchOrSubscribeButton = !!tier && needsSubscription ? /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
            value: subscriptionReferralType
        }, /*#__PURE__*/ React.createElement(PriceAndSubscribeButton, {
            tier: tier
        })) : !isOwner ? /*#__PURE__*/ React.createElement(UserWatchGhostButton, {
            author: deviation.author
        }) : null;
    }
    const typeAndItemIds = useMemo(()=>({
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId
        }), [
        deviation
    ]);
    const thumbActions = useMemo(()=>[
            ...extraActions,
            deviation.isPublished && {
                value: 'goToPage',
                label: t('deviationview.feed.cardHeader.goToPage', {
                    pageType: typeName
                }),
                link: getDeviationUrl(deviation),
                onClick: onDuperbrowseClick,
                biData: BiData(_object_spread({
                    evid: 424,
                    menu_item: 'goToPage'
                }, typeAndItemIds))
            },
            isReportable && {
                value: 'report',
                label: t('deviationview.feed.cardHeader.report'),
                onClick: ()=>requestAnimationFrame(showReportModal),
                biData: BiData(_object_spread({
                    evid: 424,
                    menu_item: 'report'
                }, typeAndItemIds))
            },
            isOwner && {
                value: 'delete',
                label: t('deviationview.feed.cardHeader.delete'),
                onClick: ()=>setShowingConfirmation(true),
                biData: BiData(_object_spread({
                    evid: 424,
                    menu_item: 'delete'
                }, typeAndItemIds))
            }
        ], [
        deviation,
        typeAndItemIds,
        extraActions,
        isReportable,
        showReportModal,
        isOwner,
        t,
        typeName,
        onDuperbrowseClick
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['root'], className)
    }, deviation.author && /*#__PURE__*/ React.createElement(UserAvatar, {
        className: s['avatar'],
        user: deviation.author,
        withTooltip: true,
        size: 32,
        relatedItemBiData: typeAndItemIds
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['left-block']
    }, deviation.author && /*#__PURE__*/ React.createElement(UserLink, {
        className: s['username'],
        user: deviation.author,
        withTooltip: true,
        relatedItemBiData: {
            typeid: deviation.typeId,
            itemid: deviation.deviationId
        }
    }), /*#__PURE__*/ React.createElement(DeviationLink, {
        className: s['timestamp'],
        deviation: deviation
    }, /*#__PURE__*/ React.createElement(Timestamp, {
        timestamp: deviation.publishedTime,
        showFullLengthOnMobile: true
    }))), /*#__PURE__*/ React.createElement("div", {
        className: s['right-block'],
        ref: duperRef
    }, watchOrSubscribeButton, thumbActions.length > 0 && /*#__PURE__*/ React.createElement(ThumbActions, {
        className: s['thumb-actions'],
        deviation: deviation,
        actions: thumbActions
    }, /*#__PURE__*/ React.createElement(IconButton, {
        icon: EllipsisIcon,
        size: IconSize.SMALL
    }))), isOwner && /*#__PURE__*/ React.createElement(ConfirmationModal, {
        isOpen: isShowingConfirmation,
        title: t('deviationview.feed.cardHeader.deleteConfirmation.confirm', {
            type: typeName
        }),
        text: t('deviationview.feed.cardHeader.deleteConfirmation.cantGoBack'),
        confirmBtnLabel: t('deviationview.feed.cardHeader.deleteConfirmation.delete'),
        icon: /*#__PURE__*/ React.createElement(PremiumTrash, null),
        confirmBtnType: "danger",
        onConfirm: ()=>{
            logBiEvent(BiData(_object_spread({
                evid: 376
            }, typeAndItemIds)));
            setShowingConfirmation(false);
            deleteDeviation();
        },
        onCancel: ()=>setShowingConfirmation(false)
    }));
};
FeedCardHeader.displayName = 'FeedCardHeader';
export default FeedCardHeader;

//# sourceMappingURL=FeedCardHeader.js.map